import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalculator, faChartPie, faQuestionCircle, faLightbulb, faStar } from '@fortawesome/free-solid-svg-icons';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

const MarginCalculatorClassic = () => {
  const [marginData, setMarginData] = useState({
    costPrice: '',
    sellingPrice: '',
    quantity: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMarginData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateMargin = () => {
    const { costPrice, sellingPrice, quantity } = marginData;
    
    const CP = parseFloat(costPrice) || 0;
    const SP = parseFloat(sellingPrice) || 0;
    const Q = parseFloat(quantity) || 0;
    
    if (CP === 0 || SP === 0 || Q === 0) {
      setError('Please enter valid numbers for all fields');
      return;
    }

    if (SP <= CP) {
      setError('Selling price must be greater than cost price');
      return;
    }

    const totalCost = CP * Q;
    const totalRevenue = SP * Q;
    const totalProfit = totalRevenue - totalCost;
    const profitMargin = (totalProfit / totalRevenue) * 100;

    const calculationSteps = [
      'Step 1: Calculate total cost',
      `Total Cost = Cost Price × Quantity`,
      `Total Cost = ${CP} × ${Q} = ${totalCost.toFixed(2)}`,
      '\nStep 2: Calculate total revenue',
      `Total Revenue = Selling Price × Quantity`,
      `Total Revenue = ${SP} × ${Q} = ${totalRevenue.toFixed(2)}`,
      '\nStep 3: Calculate total profit',
      `Total Profit = Total Revenue - Total Cost`,
      `Total Profit = ${totalRevenue.toFixed(2)} - ${totalCost.toFixed(2)} = ${totalProfit.toFixed(2)}`,
      '\nStep 4: Calculate profit margin',
      `Profit Margin = (Total Profit / Total Revenue) × 100`,
      `Profit Margin = (${totalProfit.toFixed(2)} / ${totalRevenue.toFixed(2)}) × 100 = ${profitMargin.toFixed(2)}%`
    ];

    const newResults = {
      totalCost: totalCost.toFixed(2),
      totalRevenue: totalRevenue.toFixed(2),
      totalProfit: totalProfit.toFixed(2),
      profitMargin: profitMargin.toFixed(2),
      calculationSteps
    };

    setResults(newResults);

    setChartData({
      labels: ['Cost', 'Profit'],
      datasets: [{
        label: 'Amount',
        data: [totalCost, totalProfit],
        backgroundColor: [
          'rgba(75, 192, 192, 0.5)',
          'rgba(255, 99, 132, 0.5)'
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(255, 99, 132, 1)'
        ],
        borderWidth: 1
      }]
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-blue-100 to-green-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Margin Calculator Classic | Profit Margin Tool</title>
          <meta name="description" content="Calculate your profit margins with ease. Determine total cost, revenue, profit, and profit margin percentage." />
          <meta name="keywords" content="margin calculator, profit margin, business calculator, financial tool" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-green-600 mr-4">
              <FontAwesomeIcon icon={faCalculator} />
            </div>
            <h1 className="text-3xl font-bold text-gray-800">Margin Calculator Classic</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate your profit margins with ease. This calculator helps determine total cost, revenue, profit, and profit margin percentage.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Cost Price (per unit):</label>
              <input
                type="number"
                name="costPrice"
                value={marginData.costPrice}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter cost price per unit"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Selling Price (per unit):</label>
              <input
                type="number"
                name="sellingPrice"
                value={marginData.sellingPrice}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter selling price per unit"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Quantity Sold:</label>
              <input
                type="number"
                name="quantity"
                value={marginData.quantity}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter quantity sold"
              />
            </div>
          </div>

          <button
            onClick={calculateMargin}
            className="w-full py-3 px-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="flex items-center text-lg font-semibold text-gray-700 mb-6">
                    <FontAwesomeIcon icon={faChartPie} className="mr-2 text-green-600" />
                    Financial Overview
                  </h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="bg-white p-6 rounded-lg shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-600">Total Cost</span>
                        <span className="text-green-600 font-semibold">${results.totalCost}</span>
                      </div>
                      <div className="border-t border-gray-200 pt-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Cost per unit</span>
                          <span className="text-sm text-gray-600">${marginData.costPrice}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Quantity</span>
                          <span className="text-sm text-gray-600">{marginData.quantity}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-600">Total Revenue</span>
                        <span className="text-blue-600 font-semibold">${results.totalRevenue}</span>
                      </div>
                      <div className="border-t border-gray-200 pt-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Selling price per unit</span>
                          <span className="text-sm text-gray-600">${marginData.sellingPrice}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Quantity</span>
                          <span className="text-sm text-gray-600">{marginData.quantity}</span>
                        </div>
                      </div>
                    </div>

                    <div className="col-span-2 bg-white p-6 rounded-lg shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-gray-600">Total Profit</span>
                        <span className="text-green-600 font-semibold">${results.totalProfit}</span>
                      </div>
                      <div className="border-t border-gray-200 pt-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Profit Margin</span>
                          <span className="text-green-600 font-semibold">{results.profitMargin}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="flex items-center text-lg font-semibold text-gray-700 mb-4">
                    <FontAwesomeIcon icon={faChartPie} className="mr-2 text-green-600" />
                    Profit Distribution
                  </h3>
                  <Pie
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Profit Distribution',
                          color: '#374151',
                          font: {
                            size: 16,
                            weight: 'bold'
                          }
                        },
                        legend: {
                          position: 'bottom',
                          labels: {
                            font: {
                              size: 14
                            }
                          }
                        }
                      },
                      layout: {
                        padding: {
                          top: 20,
                          bottom: 40,
                          left: 20,
                          right: 20
                        }
                      },
                      height: 400,
                      width: 400,
                      maintainAspectRatio: true
                    }}
                  />
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="calculator-section mt-12">
          <h2 className="flex items-center text-2xl font-bold text-gray-800 mb-6">
            <FontAwesomeIcon icon={faQuestionCircle} className="mr-2 text-blue-600" />
            How to Use
          </h2>
          <div className="text-gray-600 bg-white rounded-lg shadow-md p-6">
            <p>Follow these steps to use the Margin Calculator Classic:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Financial Data:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the cost price per unit</li>
                  <li>Input the selling price per unit</li>
                  <li>Input the quantity sold</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>Total cost</li>
                  <li>Total revenue</li>
                  <li>Total profit</li>
                  <li>Profit margin percentage</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the financial overview</li>
                  <li>Check the profit distribution pie chart</li>
                  <li>View the detailed calculation steps</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Ensure accurate data entry for precise results</li>
                <li>Use the results to optimize pricing strategies</li>
                <li>Regularly update calculations for ongoing financial analysis</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="flex items-center text-2xl font-bold text-gray-800 mb-6">
            <FontAwesomeIcon icon={faQuestionCircle} className="mr-2 text-blue-600" />
            FAQ
          </h2>
          <div className="text-gray-600 bg-white rounded-lg shadow-md p-6">
            <details className="mb-4">
              <summary className="font-semibold text-blue-600 cursor-pointer">What is a profit margin?</summary>
              <p className="mt-2">Profit margin is the percentage of revenue that remains after deducting all costs. It indicates the profitability of a business.</p>
            </details>
            <details className="mb-4">
              <summary className="font-semibold text-blue-600 cursor-pointer">How is profit margin calculated?</summary>
              <p className="mt-2">Profit margin is calculated using the formula: Profit Margin = (Total Profit / Total Revenue) × 100.</p>
            </details>
            <details className="mb-4">
              <summary className="font-semibold text-blue-600 cursor-pointer">Why is profit margin important?</summary>
              <p className="mt-2">Profit margin is crucial for understanding the financial health of a business. It helps in making informed decisions about pricing, cost control, and overall business strategy.</p>
            </details>
            <details className="mb-4">
              <summary className="font-semibold text-blue-600 cursor-pointer">Can I use this calculator for different types of businesses?</summary>
              <p className="mt-2">Yes, this calculator can be used for any business that sells products or services. Just input the relevant cost price, selling price, and quantity sold.</p>
            </details>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="flex items-center text-2xl font-bold text-gray-800 mb-6">
            <FontAwesomeIcon icon={faStar} className="mr-2 text-yellow-500" />
            Features & Benefits
          </h2>
          <div className="text-gray-600 bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Features</h3>
            <ul className="list-disc pl-6 mt-4 space-y-2">
              <li className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-500" />
                Simple and intuitive interface
              </li>
              <li className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-500" />
                Instant calculation of total cost, revenue, profit, and profit margin
              </li>
              <li className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-500" />
                Visual representation of profit distribution with a pie chart
              </li>
              <li className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-500" />
                Detailed calculation steps for transparency and understanding
              </li>
              <li className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-500" />
                Responsive design for use on any device
              </li>
            </ul>
            <h3 className="text-lg font-semibold text-gray-700 mt-8 mb-4">Benefits</h3>
            <ul className="list-disc pl-6 mt-4 space-y-2">
              <li className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-500" />
                Quickly assess the financial performance of your business
              </li>
              <li className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-500" />
                Optimize pricing strategies to maximize profit margins
              </li>
              <li className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-500" />
                Make informed business decisions with accurate financial data
              </li>
              <li className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-500" />
                Track financial trends over time to improve business efficiency
              </li>
              <li className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="mr-2 text-yellow-500" />
                Save time and effort with automated calculations
              </li>
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MarginCalculatorClassic;